import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import PhygitalIcon from "@/components/PhygitalIcon";
import {
  ChevronRight,
  ChevronDown,
  Check,
  Calendar,
  Clock,
  Music,
  Volume2,
  Coffee,
  Sparkles,
  Heart,
  MessageSquare,
  Upload,
  ArrowLeft,
  Send
} from "lucide-react";
import { toast } from "sonner";

// Hair specific preferences
const HAIR_EDGES_OPTIONS = [
  { id: "no_edges", label: "No edges", desc: "Natural hairline, no styling" },
  { id: "edges", label: "Edges", desc: "Styled edges/baby hairs" },
  { id: "dramatic_edges", label: "Dramatic edges", desc: "Bold, artistic edge styling" }
];

const HAIR_LENGTH_OPTIONS = [
  { id: "shoulder", label: "Shoulder length" },
  { id: "mid_back", label: "Mid back" },
  { id: "lower_back", label: "Lower back" },
  { id: "beneath_butt", label: "Beneath the butt" }
];

const HAIR_CURLS_OPTIONS = [
  { id: "no_curls", label: "No curls", desc: "Straight ends" },
  { id: "curls", label: "Curls at the end", desc: "Curled/styled ends" }
];

// Conversation mood preferences
const CONVERSATION_MOOD_OPTIONS = [
  { id: "chat", label: "Let's chat!", desc: "I love conversation", icon: MessageSquare },
  { id: "little_chat", label: "Little chat", desc: "Some conversation is nice", icon: MessageSquare },
  { id: "quiet", label: "Quiet please", desc: "I prefer silence", icon: Volume2 },
  { id: "tired", label: "I'm tired", desc: "Want to relax/sleep", icon: Coffee }
];

// Experience personalization options - "Ways to make your experience, yours"
const EXPERIENCE_OPTIONS = [
  { id: "control_music", label: "Control the music", desc: "I'd like to play my own playlist", icon: Music },
  { id: "refreshments", label: "Refreshments", desc: "I'd love a drink or snack", icon: Coffee },
  { id: "extra_pampering", label: "Extra pampering", desc: "Treat me to the full experience", icon: Sparkles }
];

// Accessibility options - moved to bottom, can be "none"
const ACCESSIBILITY_OPTIONS = [
  { id: "wheelchair", label: "Wheelchair accessible", desc: "Need wheelchair-accessible space" },
  { id: "extra_time", label: "Extra time needed", desc: "May need longer appointment times" },
  { id: "hearing", label: "Hearing assistance", desc: "May need visual cues or written communication" },
  { id: "service_animal", label: "Service animal", desc: "Will have a service animal present" },
  { id: "fragrance_free", label: "Fragrance-free", desc: "Sensitive to scents and fragrances" },
  { id: "limited_mobility", label: "Limited mobility", desc: "May need help with positioning" }
];

// Invisible disabilities with detailed explanations
const INVISIBLE_DISABILITIES = [
  { id: "vocal_tics", label: "Vocal tics", desc: "I may make sudden sounds or verbal expressions - please continue service normally" },
  { id: "motor_tics", label: "Motor tics", desc: "I may have sudden movements or gestures - please continue service normally" },
  { id: "tourettes", label: "Tourette's Syndrome", desc: "I have a combination of vocal and motor tics - please continue service normally" },
  { id: "anxiety", label: "Anxiety", desc: "I may need extra patience, breaks, or reassurance" },
  { id: "chronic_pain", label: "Chronic pain", desc: "I may need to adjust my position frequently" },
  { id: "chronic_fatigue", label: "Chronic fatigue", desc: "I may need rest breaks during longer services" },
  { id: "adhd", label: "ADHD", desc: "I may need reminders or have difficulty sitting still for long periods" },
  { id: "autism", label: "Autism spectrum", desc: "I may have specific sensory or communication preferences" },
  { id: "epilepsy", label: "Epilepsy", desc: "Please be aware of potential seizures - I will share emergency info if needed" }
];

const ClientCheckin = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [stylist, setStylist] = useState(null);
  const [services, setServices] = useState([]);
  
  // Form state
  const [form, setForm] = useState({
    name: "",
    phone: "",
    selected_service: null,
    preferred_date: "",
    preferred_time: "",
    style_notes: "",
    inspo_urls: [],
    experience_prefs: [],
    // Hair specific preferences
    hair_edges: "",
    hair_length: "",
    hair_curls: "",
    conversation_mood: "",
    // Accessibility
    accessibility_needs: [],
    invisible_disabilities: [],
    additional_notes: ""
  });
  
  const [showAccessibility, setShowAccessibility] = useState(false);

  useEffect(() => {
    fetchCheckinData();
  }, [requestId]);

  useEffect(() => {
    if (user) {
      setForm(prev => ({
        ...prev,
        name: prev.name || user.name || "",
        phone: prev.phone || user.phone || ""
      }));
    }
  }, [user]);

  const fetchCheckinData = async () => {
    try {
      const response = await axios.get(`${API}/checkin/${requestId}`);
      setStylist(response.data.stylist);
      setServices(response.data.services || []);
    } catch (error) {
      console.error("Error fetching checkin data:", error);
      toast.error("Could not load stylist info");
    } finally {
      setLoading(false);
    }
  };

  const toggleExperiencePref = (id) => {
    setForm(prev => ({
      ...prev,
      experience_prefs: prev.experience_prefs.includes(id)
        ? prev.experience_prefs.filter(p => p !== id)
        : [...prev.experience_prefs, id]
    }));
  };

  const toggleAccessibility = (id) => {
    setForm(prev => ({
      ...prev,
      accessibility_needs: prev.accessibility_needs.includes(id)
        ? prev.accessibility_needs.filter(a => a !== id)
        : [...prev.accessibility_needs, id]
    }));
  };

  const toggleDisability = (id) => {
    setForm(prev => ({
      ...prev,
      invisible_disabilities: prev.invisible_disabilities.includes(id)
        ? prev.invisible_disabilities.filter(d => d !== id)
        : [...prev.invisible_disabilities, id]
    }));
  };

  const handleSubmit = async () => {
    if (!form.name || !form.phone) {
      toast.error("Please enter your name and phone");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/checkin/${requestId}/submit`, form);
      toast.success("Preferences shared!");
      navigate(`/checkin/${requestId}/confirmed`);
    } catch (error) {
      toast.error("Could not submit preferences");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-lg mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <PhygitalIcon className="w-8 h-8" />
            <div>
              <h1 className="font-semibold text-sm">Check in with {stylist?.business_name || "your stylist"}</h1>
              <p className="text-xs text-gray-600">Share your preferences</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6">
        {/* Progress */}
        <div className="flex items-center gap-2 mb-6">
          {[1, 2, 3].map((s) => (
            <div 
              key={s}
              className={`h-1 flex-1 rounded-full transition-colors ${
                s <= step ? 'bg-gray-900' : 'bg-gray-200'
              }`}
            />
          ))}
        </div>

        {/* Step 1: Basic Info */}
        {step === 1 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold mb-1">Let&apos;s get started</h2>
              <p className="text-gray-600 text-sm">Tell {stylist?.business_name?.split(' ')[0] || "us"} a bit about yourself</p>
            </div>

            <div className="space-y-4">
              <div>
                <Label>Your Name</Label>
                <Input
                  placeholder="First name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Phone Number</Label>
                <Input
                  placeholder="For appointment reminders"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="mt-1"
                />
              </div>

              {services.length > 0 && (
                <div>
                  <Label>What service are you booking?</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {services.map((service, idx) => (
                      <button
                        key={idx}
                        onClick={() => setForm({ ...form, selected_service: service })}
                        className={`p-3 rounded-xl border text-left transition-colors ${
                          form.selected_service?.service_id === service.service_id
                            ? 'border-gray-900 bg-gray-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <p className="font-medium text-sm">{service.name}</p>
                        <p className="text-xs text-gray-600">${service.price} • {service.duration}min</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Preferred Date</Label>
                  <Input
                    type="date"
                    value={form.preferred_date}
                    onChange={(e) => setForm({ ...form, preferred_date: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Preferred Time</Label>
                  <Input
                    type="time"
                    value={form.preferred_time}
                    onChange={(e) => setForm({ ...form, preferred_time: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            <Button 
              onClick={() => setStep(2)} 
              className="w-full bg-black hover:bg-gray-800"
              disabled={!form.name || !form.phone}
            >
              Continue
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}

        {/* Step 2: Style Preferences */}
        {step === 2 && (
          <div className="space-y-6">
            <button onClick={() => setStep(1)} className="flex items-center gap-1 text-sm text-gray-600">
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>

            <div>
              <h2 className="text-xl font-bold mb-1">Style and Inspiration</h2>
              <p className="text-gray-600 text-sm">Help your stylist understand what you&apos;re looking for</p>
            </div>

            <div className="space-y-4">
              <div>
                <Label>What look are you going for?</Label>
                <Textarea
                  placeholder="Describe your desired style, any reference images you have in mind..."
                  value={form.style_notes}
                  onChange={(e) => setForm({ ...form, style_notes: e.target.value })}
                  rows={3}
                  className="mt-1"
                />
              </div>

              {/* Hair Preferences Section */}
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 space-y-4">
                <h3 className="font-medium text-sm text-amber-900">Hair Preferences</h3>
                
                {/* Edges */}
                <div>
                  <Label className="text-xs text-amber-700">How do you want your edges?</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {HAIR_EDGES_OPTIONS.map((option) => (
                      <button
                        key={option.id}
                        onClick={() => setForm({ ...form, hair_edges: option.id })}
                        className={`px-3 py-2 rounded-full text-xs font-medium transition-colors ${
                          form.hair_edges === option.id
                            ? 'bg-amber-500 text-white'
                            : 'bg-white text-amber-700 border border-amber-200 hover:border-amber-300'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Length */}
                <div>
                  <Label className="text-xs text-amber-700">Desired length</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {HAIR_LENGTH_OPTIONS.map((option) => (
                      <button
                        key={option.id}
                        onClick={() => setForm({ ...form, hair_length: option.id })}
                        className={`px-3 py-2 rounded-full text-xs font-medium transition-colors ${
                          form.hair_length === option.id
                            ? 'bg-amber-500 text-white'
                            : 'bg-white text-amber-700 border border-amber-200 hover:border-amber-300'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Curls */}
                <div>
                  <Label className="text-xs text-amber-700">Curls at the end?</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {HAIR_CURLS_OPTIONS.map((option) => (
                      <button
                        key={option.id}
                        onClick={() => setForm({ ...form, hair_curls: option.id })}
                        className={`px-3 py-2 rounded-full text-xs font-medium transition-colors ${
                          form.hair_curls === option.id
                            ? 'bg-amber-500 text-white'
                            : 'bg-white text-amber-700 border border-amber-200 hover:border-amber-300'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Conversation Mood */}
              <div className="bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200 rounded-xl p-4">
                <h3 className="font-medium text-sm text-violet-900 mb-3">What's your vibe today?</h3>
                <div className="grid grid-cols-2 gap-2">
                  {CONVERSATION_MOOD_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => setForm({ ...form, conversation_mood: option.id })}
                      className={`flex items-center gap-2 p-3 rounded-xl border transition-colors ${
                        form.conversation_mood === option.id
                          ? 'border-violet-500 bg-violet-100'
                          : 'border-violet-200 bg-white hover:border-violet-300'
                      }`}
                    >
                      <option.icon className={`w-4 h-4 ${
                        form.conversation_mood === option.id ? 'text-violet-600' : 'text-violet-400'
                      }`} />
                      <div className="text-left">
                        <p className="font-medium text-xs text-violet-900">{option.label}</p>
                        <p className="text-xs text-violet-500">{option.desc}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label>Inspiration Links (Pinterest, Instagram, etc.)</Label>
                <div className="mt-1 space-y-2">
                  <Input
                    placeholder="Paste a link..."
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.target.value) {
                        setForm({ 
                          ...form, 
                          inspo_urls: [...form.inspo_urls, e.target.value]
                        });
                        e.target.value = '';
                      }
                    }}
                  />
                  {form.inspo_urls.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {form.inspo_urls.map((url, idx) => (
                        <Badge 
                          key={idx} 
                          variant="secondary"
                          className="cursor-pointer"
                          onClick={() => setForm({
                            ...form,
                            inspo_urls: form.inspo_urls.filter((_, i) => i !== idx)
                          })}
                        >
                          Link {idx + 1} ×
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Ways to make your experience, yours */}
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <h3 className="font-medium text-sm mb-3">Ways to make your experience, yours</h3>
                <div className="space-y-2">
                  {EXPERIENCE_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => toggleExperiencePref(option.id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                        form.experience_prefs.includes(option.id)
                          ? 'border-gray-900 bg-gray-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <option.icon className={`w-5 h-5 ${
                        form.experience_prefs.includes(option.id) ? 'text-gray-900' : 'text-gray-500'
                      }`} />
                      <div className="flex-1 text-left">
                        <p className="font-medium text-sm">{option.label}</p>
                        <p className="text-xs text-gray-600">{option.desc}</p>
                      </div>
                      {form.experience_prefs.includes(option.id) && (
                        <Check className="w-4 h-4 text-green-500" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <Button 
              onClick={() => setStep(3)} 
              className="w-full bg-black hover:bg-gray-800"
            >
              Continue
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}

        {/* Step 3: Accessibility and Confirm */}
        {step === 3 && (
          <div className="space-y-6">
            <button onClick={() => setStep(2)} className="flex items-center gap-1 text-sm text-gray-600">
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>

            <div>
              <h2 className="text-xl font-bold mb-1">Almost done!</h2>
              <p className="text-gray-600 text-sm">Any additional info for your stylist?</p>
            </div>

            <div className="space-y-4">
              <div>
                <Label>Additional Notes</Label>
                <Textarea
                  placeholder="Anything else your stylist should know?"
                  value={form.additional_notes}
                  onChange={(e) => setForm({ ...form, additional_notes: e.target.value })}
                  rows={2}
                  className="mt-1"
                />
              </div>

              {/* Accessibility Section - Collapsible */}
              <div className="bg-purple-50 border border-purple-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setShowAccessibility(!showAccessibility)}
                  className="w-full flex items-center justify-between p-4"
                >
                  <div className="flex items-center gap-3">
                    <Heart className="w-5 h-5 text-purple-600" />
                    <div className="text-left">
                      <p className="font-medium text-sm text-purple-900">Accessibility and Comfort</p>
                      <p className="text-xs text-purple-600">
                        {form.accessibility_needs.length + form.invisible_disabilities.length > 0
                          ? `${form.accessibility_needs.length + form.invisible_disabilities.length} selected`
                          : "Optional - skip if none apply"
                        }
                      </p>
                    </div>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-purple-400 transition-transform ${showAccessibility ? 'rotate-180' : ''}`} />
                </button>

                {showAccessibility && (
                  <div className="px-4 pb-4 space-y-4 border-t border-purple-200">
                    {/* Accessibility Needs */}
                    <div className="pt-4">
                      <p className="text-xs font-medium text-purple-800 mb-2">Physical Accessibility</p>
                      <div className="grid grid-cols-2 gap-2">
                        {ACCESSIBILITY_OPTIONS.map((option) => (
                          <button
                            key={option.id}
                            onClick={() => toggleAccessibility(option.id)}
                            className={`p-2 rounded-lg border text-left transition-colors ${
                              form.accessibility_needs.includes(option.id)
                                ? 'border-purple-400 bg-purple-100'
                                : 'border-purple-200 hover:border-purple-300 bg-white'
                            }`}
                          >
                            <p className="font-medium text-xs">{option.label}</p>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Invisible Disabilities */}
                    <div>
                      <p className="text-xs font-medium text-purple-800 mb-2">Invisible Disabilities</p>
                      <p className="text-xs text-purple-600 mb-3">
                        These are shared confidentially to help your stylist provide the best care.
                      </p>
                      <div className="space-y-2">
                        {INVISIBLE_DISABILITIES.map((option) => (
                          <button
                            key={option.id}
                            onClick={() => toggleDisability(option.id)}
                            className={`w-full p-3 rounded-lg border text-left transition-colors ${
                              form.invisible_disabilities.includes(option.id)
                                ? 'border-purple-400 bg-purple-100'
                                : 'border-purple-200 hover:border-purple-300 bg-white'
                            }`}
                          >
                            <p className="font-medium text-sm">{option.label}</p>
                            <p className="text-xs text-purple-600 mt-0.5">{option.desc}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Summary */}
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <h3 className="font-medium text-sm mb-3">Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Name</span>
                  <span className="font-medium">{form.name}</span>
                </div>
                {form.selected_service && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Service</span>
                    <span className="font-medium">{form.selected_service.name}</span>
                  </div>
                )}
                {form.preferred_date && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Preferred Date</span>
                    <span className="font-medium">{form.preferred_date}</span>
                  </div>
                )}
                {form.experience_prefs.length > 0 && (
                  <div className="pt-2 border-t border-gray-100">
                    <span className="text-gray-600 text-xs">Experience preferences:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {form.experience_prefs.map(id => {
                        const opt = EXPERIENCE_OPTIONS.find(o => o.id === id);
                        return (
                          <Badge key={id} variant="secondary" className="text-xs">
                            {opt?.label}
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <Button 
              onClick={handleSubmit} 
              className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700"
              disabled={submitting}
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Sending...
                </span>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Share Preferences
                </>
              )}
            </Button>

            <p className="text-xs text-gray-500 text-center">
              Your stylist will confirm your appointment details
            </p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 py-3">
        <div className="flex items-center justify-center gap-1 text-gray-500 text-xs">
          <PhygitalIcon className="w-4 h-4" showBox={false} />
          <span>Powered by phygital</span>
        </div>
      </footer>
    </div>
  );
};

export default ClientCheckin;
